// Getting someone into your game without reading four letters down the phone.
//
// The code still works and is still shown — a link is no use to the person
// standing next to you. This is for the one who is not in the room.

import { shareOrigin } from '../core/native.js';
import { normaliseCode, CODE_LEN } from './duel.js';

/** The query parameter a join link carries. */
export const JOIN_PARAM = 'join';

/**
 * The link to send.
 *
 * Always built from `shareOrigin()`, never `location.origin`: in the app the
 * page came from the Capacitor bundle, and a link to that origin is one only
 * the sender's phone could ever open.
 *
 * It points at the WEB app on purpose. The recipient probably does not have
 * the app, and asking them to install one before they can play a four-minute
 * game is how an invitation gets ignored.
 */
export function inviteLink(code, origin = shareOrigin()) {
  const clean = normaliseCode(code);
  if (!clean) return '';
  return `${String(origin).replace(/\/+$/, '')}/app?${JOIN_PARAM}=${clean}`;
}

/**
 * What the message says.
 *
 * The characters are named by whatever the host called them, because that is
 * what they will see when they arrive, and an invitation that describes a
 * different game than the one you open is a small broken promise.
 *
 * The code is in the text as well as the link. Plenty of apps strip a URL out
 * of a preview, or the link gets forwarded as plain text, and a message that
 * still tells you the code survives all of that.
 */
export function inviteText(code, { nick = 'Nick', raf = 'Raf' } = {}) {
  const clean = normaliseCode(code);
  if (!clean) return '';
  return `Play PizzaRaf with me. I'm ${nick}, you're ${raf} — chase me round the maze `
    + `before I eat all the pizza. Tap to join, or enter the code ${clean}.`;
}

/** The title the share sheet shows above it. */
export const INVITE_TITLE = 'Join my PizzaRaf game';

/**
 * The code in a link somebody opened, or '' if there is not one.
 *
 * Anything unusable comes back as '' rather than a partial code — a guest
 * dropped on the home screen with three letters typed in is worse than one
 * dropped there with none.
 */
export function codeFromUrl(url) {
  let search;
  try { search = new URL(String(url), 'https://rafman.invalid').searchParams; }
  catch { return ''; }
  const code = normaliseCode(search.get(JOIN_PARAM));
  return code.length === CODE_LEN ? code : '';
}
